import type { MfaEnrolment, UnifiedAuthStep } from '~kui/types/auth';
import type { Ref } from 'vue';

import type { LoginFlowType, VerifyAdminMfaInput } from '~/graphql/generated/graphql';

import type { UnifiedAuthResetOptions } from './useUnifiedAuthFlow';

import { useAdminMfa } from './useAdminMfa';

/** A second-factor step's result: an error is a message for the step's form, or `access-denied` for the banner. */
export interface SecondFactorResult {
  flow: LoginFlowType | null;
  error: string | null;
}

interface AuthSecondFactorOptions {
  loading: Ref<boolean>;
  currentStep: Ref<UnifiedAuthStep>;
  goToStep: (step: UnifiedAuthStep) => void;
  /** Completes the login the second factor was holding back. */
  completeExistingLogin: () => Promise<SecondFactorResult>;
  /** Returns the rest of the flow to a fresh email step; the second-factor state is cleared here. */
  reset: (options?: UnifiedAuthResetOptions) => void;
}

/**
 * The second-factor half of the unified auth flow: the enrolment and challenge steps an admin
 * passes after the first factor, split out the way `useAuthPasswordLogin` extracts the password
 * concern. Where a verified second factor leads stays with the owner flow.
 */
export const useAuthSecondFactor = ({
  loading,
  currentStep,
  goToStep,
  completeExistingLogin,
  reset,
}: AuthSecondFactorOptions) => {
  const { logout, user } = useAuth();
  const adminMfa = useAdminMfa();

  const mfaEnrolment = ref<MfaEnrolment | null>(null);
  const mfaRecoveryCodes = ref<string[] | null>(null);
  const mfaLoadFailed = ref(false);
  /** The pending sign-in lapsed (or the session ended) under a second-factor step; only starting again moves on. */
  const mfaExpired = ref(false);
  const mfaSignedIn = computed(() => !!user.value);

  const clear = () => {
    mfaEnrolment.value = null;
    mfaRecoveryCodes.value = null;
    mfaLoadFailed.value = false;
    mfaExpired.value = false;
  };

  const beginMfaEnrolment = async () => {
    loading.value = true;
    mfaLoadFailed.value = false;
    mfaEnrolment.value = null;
    mfaRecoveryCodes.value = null;

    const outcome = await adminMfa.beginEnrolment();

    switch (outcome.kind) {
      case 'alreadyEnrolled':
        goToStep('mfa-challenge');
        break;
      case 'enrolmentStarted':
        mfaEnrolment.value = outcome.enrolment;
        break;
      case 'expired':
        mfaExpired.value = true;
        break;
      default:
        mfaLoadFailed.value = true;
    }

    loading.value = false;
  };

  const mfaEnrolSubmitCode = async (code: string): Promise<SecondFactorResult> => {
    loading.value = true;

    const outcome = await adminMfa.confirmEnrolment({ code });
    loading.value = false;

    switch (outcome.kind) {
      case 'enrolled':
        mfaRecoveryCodes.value = outcome.recoveryCodes;
        break;
      case 'alreadyEnrolled':
        goToStep('mfa-challenge');
        break;
      case 'expired':
        mfaExpired.value = true;
        break;
      case 'invalidCode':
        return { flow: null, error: outcome.message };
    }

    return { flow: null, error: null };
  };

  const mfaRecoveryCodesSaved = async () => {
    loading.value = true;
    return await completeExistingLogin();
  };

  const verifySecondFactor = async (input: VerifyAdminMfaInput): Promise<SecondFactorResult> => {
    loading.value = true;

    const outcome = await adminMfa.verify(input);

    switch (outcome.kind) {
      case 'verified':
        return await completeExistingLogin();
      case 'enrolmentRequired':
        goToStep('mfa-enrol');
        break;
      case 'expired':
        mfaExpired.value = true;
        break;
      case 'invalidCode':
        loading.value = false;
        return { flow: null, error: outcome.message };
    }

    loading.value = false;
    return { flow: null, error: null };
  };

  const mfaSubmitCode = (code: string) => verifySecondFactor({ code });

  const mfaSubmitRecoveryCode = (recoveryCode: string) => verifySecondFactor({ recoveryCode });

  /**
   * A session that is still pending its second factor is signed out in place, back to a fresh
   * login. The email goes too, even one that arrived in the link: a prefilled email step has never
   * been touched, so its submit stays disabled.
   */
  const mfaSignOut = async () => {
    loading.value = true;
    await logout(false);
    loading.value = false;
    clear();
    reset({ keepPrefilledEmail: false });
  };

  // The enrolment step needs a fresh authenticator secret each time it is shown, and the server
  // mints one per request. An opening step is entered on mount, so the request never runs on SSR.
  watch(currentStep, (step) => {
    if (step === 'mfa-enrol') {
      void beginMfaEnrolment();
    }
  });
  if (currentStep.value === 'mfa-enrol') {
    onMounted(() => {
      void beginMfaEnrolment();
    });
  }

  return {
    mfaEnrolment,
    mfaRecoveryCodes,
    mfaLoadFailed,
    mfaExpired,
    mfaSignedIn,
    clear,
    beginMfaEnrolment,
    mfaEnrolSubmitCode,
    mfaRecoveryCodesSaved,
    mfaSubmitCode,
    mfaSubmitRecoveryCode,
    mfaSignOut,
  };
};
